// @ts-nocheck
import { Component } from '@theme/component';
import { onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * R-TAP-ANIM (17 Sep 2026): MODULE-LEVEL, NOT A METHOD ON <add-to-cart-component>.
 *
 * It was a method first, and it silently did nothing on the surface it was written
 * for. `blocks/ctr-buy-buttons.liquid` - the block Build-a-Box cards use - has NO
 * <add-to-cart-component> at all; only v2 and the two Best Sellers sections do. So
 * `refs.addToCartButtonContainer` was undefined on every Build-a-Box card and the
 * optional call no-opped. Caught by looking for the element after a tap and
 * finding none, not by reading the diff.
 *
 * @param {Element | null | undefined} sourceButton - what the circle flies from.
 * @param {string | null} image
 */
export function launchFlyToCart(sourceButton, image) {
  const cartIcon = document.querySelector('.header-actions__cart-icon');

  if (!cartIcon || !sourceButton || !image) return;

  // A source with no box gives a start point derived from 0,0 - the circle flies
  // in from above the viewport. Reachable whenever the add came from something
  // the customer cannot see: a card whose product is already in the basket has its
  // Add to cart hidden and its stepper showing, and the first-paint shim replays a
  // submit against a form whose button may since have been hidden by a morph.
  const sourceRect = sourceButton.getBoundingClientRect();
  if (!sourceRect.width || !sourceRect.height) return;

  const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

  flyToCartElement.style.setProperty('background-image', `url(${image})`);
  flyToCartElement.source = sourceButton;
  flyToCartElement.destination = cartIcon;

  document.body.appendChild(flyToCartElement);
}

/**
 * The product photo the card is already showing.
 *
 * Read off the rendered <img> rather than built from `image_url` in Liquid, for
 * two reasons: `data-product-variant-media` is empty on every card in this theme
 * (`product_variant_media` is referenced and never assigned), and `currentSrc` is
 * the exact srcset candidate the browser has ALREADY downloaded - so the animation
 * costs no request. Building a fresh width would be a second fetch of a picture
 * that is on screen, which is the one thing this repository does not do
 * (STOREFRONT-RULES section 1).
 * @param {Element | null | undefined} from
 * @returns {string | null}
 */
export function nearestCardImage(from) {
  let node = from?.parentElement;

  for (let depth = 0; node && depth < 8; depth += 1, node = node.parentElement) {
    for (const img of node.querySelectorAll('img')) {
      // Skip badges, icons and anything not yet decoded.
      if (img.naturalWidth && img.naturalWidth < 40) continue;
      const src = img.currentSrc || img.src;
      if (src) return src;
    }
  }

  return null;
}

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    this.animateAddToCart();

    if (!event.target.closest('.quick-add-modal')) this.flyToCart();
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Flies a small circle of the product's own photo into the header cart icon.
   * Kept for `handleClick`, which nothing wires; `handleSubmit` calls the module
   * helper directly because most surfaces have no <add-to-cart-component>.
   */
  flyToCart() {
    launchFlyToCart(
      this.refs.addToCartButton,
      this.dataset.productVariantMedia || nearestCardImage(this)
    );
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Handles the submit event for the product form.
   *
   * R57c-4: the last initiator hand-rolling its own round trip. It built the
   * body, fetched, parsed, dispatched a CartAddEvent carrying the response
   * sections and then a store update - its own version of the same twenty
   * lines the other six initiators each had a slightly different copy of.
   * cartController.add() owns all of it now, which fixes three things on the
   * way past:
   *
   * 1. This path never sent `sections_url`, so the drawer it asked Shopify to
   *    re-render was rendered against the default template rather than the page
   *    the customer is standing on. withSections() sends it on every mutation.
   * 2. A response that came back without usable sections (bundled section
   *    rendering returns null per section on a render failure) morphed nothing
   *    and published nothing. The controller reconciles instead.
   * 3. The error branch dispatched an itemCount of the quantity the customer
   *    ASKED for, on a response whose whole point is that the back end added
   *    fewer. The controller reconciles from the server and publishes the truth.
   *
   * The badge arithmetic moved with it: see the R57c-4 note in cart-icon.js.
   * @param {Event} event - The submit event.
   */
  async handleSubmit(event) {
    // Stop default behaviour from the browser
    event.preventDefault();

    // Check if the add to cart button is disabled and do an early return if it is
    if (this.refs.addToCartButtonContainer?.refs.addToCartButton?.getAttribute('disabled') === 'true') return;

    // R-TAP (17 Sep 2026): the tap-time and settle-time signals a surface needs
    // to react to an add BEFORE the round trip finishes. `cart-add-start` fires
    // in the same task as the tap, so a product card can flip straight to its
    // stepper; `cart-add-settle` carries whether it worked, so the card can put
    // itself back if it did not. Dispatched on this element, bubbling, so an
    // Alpine root wrapping the form hears them without any markup change and
    // two cards on the same page cannot hear each other's.
    //
    // Deliberately NOT a second cart pipeline: nothing here mutates anything,
    // and cartController.add() remains the only route to /cart/add.js.
    let added = false;
    this.dispatchEvent(new CustomEvent('cart-add-start', { bubbles: true }));

    // R-TAP, Codex round TAP-5 finding 1: this path opens the drawer on the tap
    // too. It is the standard buy box on six product templates and every product
    // card, and it was the one add route still waiting for the response before
    // anything happened on screen.
    //
    // `respectAutoOpen` because, unlike the three PDP buy boxes, this path has
    // never opened the drawer itself - #handleCartAdd does, and only on the
    // conditions it checks. Opening early has to fire on exactly those occasions
    // or Build-a-Box gets a drawer over its grid on every card tap.
    // R-TAP-ANIM: the drawer decides. It opens on the tap where the auto-open rule
    // says it should, and where it does not - every add made with something
    // already in the basket - it flies the product's own photo to the cart icon
    // instead, so the tap is never answered by nothing. One decision point for
    // every add path; see openForPendingAdd() in assets/cart-drawer.js.
    const cartDrawer = /** @type {any} */ (document.querySelector('cart-drawer-component'));
    cartDrawer?.openForPendingAdd?.({
      source:
        this.refs.addToCartButtonContainer?.refs?.addToCartButton ??
        this.querySelector('button[name="add"]'),
    });

    try {
      const form = this.querySelector('form');

      if (!form) throw new Error('Product form element missing');

      const formData = new FormData(form);
      const id = formData.get('id');

      if (!id) throw new Error('Form ID is required');

      // No `sections` append here any more. R57b appended the registry ids by
      // hand; withSections() in the controller appends them AND sections_url on
      // every mutation, so there is one place that decides what a cart request
      // asks for.
      const body = this.#convertFormDataToBody(formData);

      if (!window.cartController?.add) {
        // Codex P2 from the R57c-1 review, applied to this path: a missing
        // controller must say so rather than rejecting into nothing and leaving
        // the customer looking at a button that appeared to work.
        const error = new Error(`cart-controller.js is not loaded; no cart request was sent for variant ${id}`);
        console.error('[product-form]', error.message);
        window.ctrNotifyCartFailure(
          'Sorry, your basket could not be updated. Please try again.',
          'product-form',
          error
        );
        return;
      }

      const result = await window.cartController.add(body, { source: 'product-form' });

      if (!result.ok) {
        // R90b, Codex round 35 finding 8. cartController.add() has ALREADY
        // surfaced this rejection - banner plus live region - before returning.
        // Rendering it again inline and announcing it a second time meant one
        // refused add produced two visible messages and two announcements.
        //
        // The controller owns the failure surface, exactly as it does on the
        // build-a-box path. This caller's job here is to stop, not to re-report.
        return;
      }

      added = true;
      this.#clearAddToCartError();
      this.#announceAdded();

      // quick-add's dialog closes itself on a CartAddEvent bubbling out of the
      // form inside it. The controller dispatches on `document` instead, which
      // never reaches the dialog, so close it from here. Inert today -
      // settings.quick_add and settings.mobile_quick_add are both false so the
      // modal is never rendered - but it stops the feature coming back broken.
      const quickAddDialog = /** @type {any} */ (this.closest('quick-add-dialog'));
      quickAddDialog?.closeDialog?.();
    } catch (error) {
      // R90b, Codex round 35 finding 7. Everything above this line can throw:
      // the missing-form and missing-id guards, #convertFormDataToBody, and a
      // rejected cartController.add() promise. All of them ended here, in a
      // console the customer cannot see, with the button already animated as
      // though the add had worked. An indeterminate add is a failed add.
      console.error('[product-form] add to cart failed', error);
      window.ctrNotifyCartFailure?.(
        'Sorry, your basket could not be updated. Please try again.',
        'product-form',
        error
      );
    } finally {
      // Nothing is coming to lift the pending mark when the add was refused
      // without a reconcile - the subscription guard's path.
      cartDrawer?.clearPendingAdd?.();
      this.dispatchEvent(new CustomEvent('cart-add-settle', { bubbles: true, detail: { ok: added } }));
      cartPerformance.measureFromEvent('add:user-action', event);
    }
  }

  /**
   * Hides any error left over from a previous attempt.
   */
  #clearAddToCartError() {
    const { addToCartTextError } = this.refs;

    if (!addToCartTextError) return;

    addToCartTextError.classList.add('hidden');
    addToCartTextError.removeAttribute('aria-live');
  }

  /**
   * Announces the successful add to screen readers, reusing whatever the button
   * itself says so the locale-translated string is never duplicated in JS.
   */
  #announceAdded() {
    const addToCartButton = this.refs.addToCartButtonContainer?.refs.addToCartButton;

    if (!addToCartButton) return;

    const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
    const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

    this.#setLiveRegionText(addedText);

    setTimeout(() => {
      this.#clearLiveRegionText();
    }, 5000);
  }

  #convertFormDataToBody(formData, options = {}) {

    const { 
      typeConversions = { '_dynamicBoxIndex': parseInt },
      propertiesPrefix = 'properties['
    } = options;
    

    const body = { properties: {} };
    
    formData.forEach((value, key) => {
      if (key.startsWith(propertiesPrefix) && key.endsWith(']')) {
        const propertyName = key.slice(propertiesPrefix.length, -1);
        
        if (typeConversions[propertyName]) {
          body.properties[propertyName] = typeConversions[propertyName](value);
        } else {
          body.properties[propertyName] = value;
        }
      } else {
        body[key] = value;
      }
    });
    
    return body;
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    if (!currentAddToCartButton) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      addToCartButtonContainer.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    } else {
      addToCartButtonContainer.enable();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
    }

    // Update the add to cart button text and icon
    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    // Update the variant ID
    variantId.value = event.detail.resource.id ?? '';

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
